// hooks/useFormValidation.js - VERSÃO ATUALIZADA
import { ref } from 'vue';
import { useStore } from 'vuex';

export function useFormValidation() {
  const store = useStore();

  // Component refs
  const selectedPublicRef = ref(null);
  const placeholdersDynamicRef = ref(null);
  const filtersRef = ref(null);

  // Validation state
  const placeholderSelections = ref({});
  const filters = ref([]);

  // Função auxiliar: Obter dados atuais do Vuex
  const getCurrentVuexData = () => {
    return store.getters['campaigns/getCampaignData'];
  };

  // Função auxiliar: Salvar dados no Vuex
  const saveToVuex = async dataToSave => {
    try {
      const currentData = getCurrentVuexData();
      const mergedData = { ...currentData, ...dataToSave };

      await store.dispatch('campaigns/saveCampaignData', mergedData);

      return true;
    } catch (error) {
      console.error('Erro ao salvar no Vuex:', error);
      return false;
    }
  };

  // Tab validation
  const validateCurrentTab = async (activeTab, isPlaceholdersValid) => {
    switch (activeTab) {
      case 'table':
        if (!selectedPublicRef.value) {
          return false;
        }

        const isPublicValid = await selectedPublicRef.value?.validateAndEmit();

        // Salvar os dados no Vuex quando a aba for válida
        if (isPublicValid) {
          const formData = {
            selectedTable: selectedPublicRef.value.selectedTable,
            selectedPhoneColumn: selectedPublicRef.value.selectedPhoneColumn,
            selectedContactColumn:
              selectedPublicRef.value.selectedContactColumn,
          };

          await saveToVuex(formData);
        }

        return isPublicValid;

      case 'placeholders':
        let isPlaceholdersValidFromComponent = true;
        if (placeholdersDynamicRef.value?.validateAndEmit) {
          isPlaceholdersValidFromComponent =
            await placeholdersDynamicRef.value.validateAndEmit();
        }

        // Validação adicional baseada nos dados atuais
        const currentSelections = placeholderSelections.value;
        const hasSelections = Object.keys(currentSelections).length > 0;

        let finalPlaceholdersValid;
        if (!hasSelections) {
          // Template sem placeholders → válido (não há nada para preencher)
          finalPlaceholdersValid = isPlaceholdersValidFromComponent;
        } else {
          const allPlaceholdersFilled = Object.values(currentSelections).every(
            value => value && value.trim() !== ''
          );
          finalPlaceholdersValid =
            allPlaceholdersFilled && isPlaceholdersValidFromComponent;
        }

        // Atualizar a ref de validação se fornecida
        if (isPlaceholdersValid) {
          isPlaceholdersValid.value = finalPlaceholdersValid;
        }

        // Salvar placeholders no Vuex
        if (finalPlaceholdersValid) {
          await saveToVuex({
            placeholders: { ...placeholderSelections.value },
          });
        }

        return finalPlaceholdersValid;

      case 'filters':
        // Salvar filtros no Vuex
        await saveToVuex({ filters: [...filters.value] });
        return true;

      default:
        return true;
    }
  };

  // Event handlers
  const onPlaceholdersSubmit = async selections => {
    if (!selections || typeof selections !== 'object') {
      return;
    }

    // Atualizar estado local
    placeholderSelections.value = { ...selections };

    // Salvar no Vuex
    await saveToVuex({ placeholders: { ...selections } });
  };

  const onPlaceholdersChange = async (selections, isPlaceholdersValid) => {
    if (!selections || typeof selections !== 'object') {
      return;
    }

    // Atualizar estado local
    placeholderSelections.value = { ...selections };

    // Verificar se todos os placeholders estão preenchidos
    const hasSelections = Object.keys(selections).length > 0;
    const allFilled = hasSelections
      ? Object.values(selections).every(value => value && value.trim() !== '')
      : true; // Sem placeholders = válido
    const isValid = allFilled;

    if (isPlaceholdersValid) {
      isPlaceholdersValid.value = isValid;
    }

    // Salvar automaticamente no Vuex
    await saveToVuex({ placeholders: { ...selections } });
  };

  const onFiltersChange = async newFilters => {
    const safeFilters = Array.isArray(newFilters) ? newFilters : [];

    // Atualizar estado local
    filters.value = [...safeFilters];

    // Salvar automaticamente no Vuex
    await saveToVuex({ filters: [...safeFilters] });
  };

  const onClearFilters = async () => {
    // Limpar estado local
    filters.value = [];

    // Salvar no Vuex
    await saveToVuex({ filters: [] });

    // 🔧 NOVO: Limpar também no componente se disponível
    if (filtersRef.value) {
      if (filtersRef.value.clearAllFilters) {
        filtersRef.value.clearAllFilters();
      } else if (filtersRef.value.filters) {
        filtersRef.value.filters = [];
      }
    }
  };

  // 🔧 NOVO: Função específica para restaurar filtros
  const restoreFilters = async filtersToRestore => {
    if (!Array.isArray(filtersToRestore)) {
      return false;
    }

    // Atualizar estado local
    filters.value = [...filtersToRestore];

    // Tentar restaurar no componente se disponível
    if (filtersRef.value) {
      if (filtersRef.value.setFilters) {
        filtersRef.value.setFilters(filtersToRestore);
      } else if (filtersRef.value.initializeFilters) {
        filtersRef.value.initializeFilters(filtersToRestore);
      } else {
        filtersRef.value.filters = [...filtersToRestore];
      }
    }

    return true;
  };

  // 🔧 NOVO: Função específica para restaurar placeholders
  const restorePlaceholders = async placeholdersToRestore => {
    if (!placeholdersToRestore || typeof placeholdersToRestore !== 'object') {
      return false;
    }

    // Atualizar estado local
    placeholderSelections.value = { ...placeholdersToRestore };

    // Tentar restaurar no componente se disponível
    if (placeholdersDynamicRef.value) {
      if (placeholdersDynamicRef.value.setPlaceholders) {
        placeholdersDynamicRef.value.setPlaceholders(placeholdersToRestore);
      } else if (placeholdersDynamicRef.value.initializeSelections) {
        placeholdersDynamicRef.value.initializeSelections(
          placeholdersToRestore
        );
      } else {
        placeholdersDynamicRef.value.placeholderSelections = {
          ...placeholdersToRestore,
        };
      }
    }

    return true;
  };

  // Função de inicialização: Restaurar dados do Vuex
  const initializeFromVuex = () => {
    const currentData = getCurrentVuexData();

    if (currentData) {
      // Restaurar filtros
      if (currentData.filters && Array.isArray(currentData.filters)) {
        filters.value = [...currentData.filters];
      }

      // Restaurar placeholders
      if (
        currentData.placeholders &&
        typeof currentData.placeholders === 'object'
      ) {
        placeholderSelections.value = { ...currentData.placeholders };
      }
    }
  };

  return {
    // Component refs
    selectedPublicRef,
    placeholdersDynamicRef,
    filtersRef,

    // State
    placeholderSelections,
    filters,

    // Functions
    validateCurrentTab,
    onPlaceholdersSubmit,
    onPlaceholdersChange,
    onFiltersChange,
    onClearFilters,

    // 🔧 NOVAS funções específicas
    restoreFilters,
    restorePlaceholders,

    initializeFromVuex,
    getCurrentVuexData,
    saveToVuex,
  };
}
